import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { SiteFilter } from "@/components/admin/SiteFilter";
import {
  Search,
  AlertTriangle,
  Users,
  Brain,
  TrendingUp,
  TrendingDown,
  Shield,
  Network,
  Zap,
  ChevronDown,
  ChevronUp,
  Target,
  Activity,
  Sparkles,
  RefreshCw,
  Loader2,
  Link2,
  FileWarning,
  Lightbulb,
  Eye,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useUserContext } from "@/hooks/useUserContext";
import { 
  generateWorkerSummary, 
  hazardCategoryTranslations, 
  networkRoleTranslations, 
  safetyStateTranslations,
  psychometricTranslations
} from "@/utils/plainLanguageTranslations";

interface WorkerDiagnostic {
  id: string;
  user_id: string;
  display_name: string;
  full_name: string;
  team: string;
  position: string;
  current_risk_score: number;
  baseline_risk_score: number;
  psychometric_profile: Record<string, any> | null;
  network_role: string | null;
  network_centrality: number | null;
  cluster_id: string | null;
  is_wide_bridge: boolean;
  total_xp: number;
  site_id: string | null;
  site_name?: string;
  violation_count: number;
  violations: Array<{
    id: string;
    hazard_category: string;
    created_at: string;
    description: string;
    risk_rating: string;
  }>;
  risk_classification: string;
  key_risk_factors: string[];
  recommended_intervention: string | null;
  risky_neighbor_count: number;
  cluster_risk: number;
  // MDP State
  safety_state: "safe" | "at_risk" | "high_risk" | "chronic";
  state_changed_at: string | null;
}

interface DiagnosticStats {
  totalWorkers: number;
  chronicCount: number;
  highRiskCount: number;
  atRiskCount: number;
  safeCount: number;
  avgRiskScore: number;
  psychometricCoverage: number;
}

interface WorkerDiagnosticsProps {
  siteId?: string | null;
}

const WorkerDiagnostics = ({ siteId: propSiteId }: WorkerDiagnosticsProps = {}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { companyId, isLoading: contextLoading } = useUserContext();
  const [workers, setWorkers] = useState<WorkerDiagnostic[]>([]);
  const [stats, setStats] = useState<DiagnosticStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSite, setSelectedSite] = useState(propSiteId || "all");
  const [stateFilter, setStateFilter] = useState<"all" | "chronic" | "high_risk" | "at_risk" | "safe">("all");
  const [expandedWorker, setExpandedWorker] = useState<string | null>(null);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState<string | null>(null);
  const [generatingRecommendations, setGeneratingRecommendations] = useState(false);
  const [recalculatingRisk, setRecalculatingRisk] = useState<string | null>(null);
  const [recalculatingAll, setRecalculatingAll] = useState(false);

  useEffect(() => {
    if (!contextLoading && companyId) {
      fetchWorkerDiagnostics();
    }
  }, [selectedSite, contextLoading, companyId]);

  const fetchWorkerDiagnostics = async () => {
    if (!companyId) return;
    
    setIsLoading(true);
    try {
      let query = supabase
        .from("worker_profiles")
        .select(
          `
          id, user_id, display_name, full_name, team, position,
          current_risk_score, baseline_risk_score, psychometric_profile,
          network_role, network_centrality, cluster_id, is_wide_bridge, 
          total_xp, site_id, safety_state, state_changed_at,
          site:company_locations!site_id(location_name)
        `,
        )
        .eq("company_id", companyId)
        .order("current_risk_score", { ascending: false });

      if (selectedSite !== "all") {
        query = query.eq("site_id", selectedSite);
      }

      const { data: workersData, error } = await query;
      if (error) throw error;

      // Fetch violation details for all workers
      const workerIds = (workersData || []).map((w) => w.id);
      const { data: violationsData } = await supabase
        .from("peer_reports")
        .select("id, reported_worker_id, hazard_category, created_at, description, risk_rating")
        .in("reported_worker_id", workerIds)
        .in("verification_status", ["verified", "hazard_confirmed"])
        .order("created_at", { ascending: false });

      const violationsByWorker: Record<string, any[]> = {};
      (violationsData || []).forEach((v) => {
        if (!violationsByWorker[v.reported_worker_id]) {
          violationsByWorker[v.reported_worker_id] = [];
        }
        violationsByWorker[v.reported_worker_id].push(v);
      });

      // Fetch cluster risk data
      const clusterIds = [...new Set((workersData || []).filter((w) => w.cluster_id).map((w) => w.cluster_id))];
      const { data: clustersData } = await supabase
        .from("worker_clusters")
        .select("id, risk_score")
        .in("id", clusterIds);

      const clusterRisks: Record<string, number> = {};
      (clustersData || []).forEach((c: any) => {
        clusterRisks[c.id] = c.risk_score || 0;
      });

      // Count risky neighbors (workers in same cluster with high risk)
      const riskyNeighborCounts: Record<string, number> = {};
      (workersData || []).forEach((w) => {
        if (w.cluster_id) {
          const clusterMembers = (workersData || []).filter(
            (other) => other.cluster_id === w.cluster_id && other.id !== w.id,
          );
          riskyNeighborCounts[w.id] = clusterMembers.filter((m) => (m.current_risk_score || 0) >= 50).length;
        } else {
          riskyNeighborCounts[w.id] = 0;
        }
      });

      // Transform and classify workers
      const diagnostics: WorkerDiagnostic[] = (workersData || []).map((worker: any) => {
        const psychProfile = worker.psychometric_profile || {};
        const riskScore = worker.current_risk_score || 0;
        const violations = violationsByWorker[worker.id] || [];

        // Classify risk profile
        const classification = classifyRiskProfile(psychProfile, riskScore, violations.length);
        const keyFactors = getKeyRiskFactors(
          psychProfile,
          riskScore,
          worker.network_role,
          violations.length,
          worker.is_wide_bridge,
        );
        const recommendation = getRecommendedIntervention(
          classification,
          riskScore,
          worker.network_role,
          worker.is_wide_bridge,
        );

        return {
          id: worker.id,
          user_id: worker.user_id,
          display_name: worker.display_name,
          full_name: worker.full_name || "",
          team: worker.team,
          position: worker.position || "",
          current_risk_score: riskScore,
          baseline_risk_score: worker.baseline_risk_score || 0,
          psychometric_profile: psychProfile,
          network_role: worker.network_role,
          network_centrality: worker.network_centrality,
          cluster_id: worker.cluster_id,
          is_wide_bridge: worker.is_wide_bridge || false,
          total_xp: worker.total_xp || 0,
          site_id: worker.site_id,
          site_name: worker.site?.location_name,
          violation_count: violations.length,
          violations: violations.slice(0, 5), // Latest 5
          risk_classification: classification,
          key_risk_factors: keyFactors,
          recommended_intervention: recommendation,
          risky_neighbor_count: riskyNeighborCounts[worker.id] || 0,
          cluster_risk: worker.cluster_id ? clusterRisks[worker.cluster_id] || 0 : 0,
          safety_state: worker.safety_state || "safe",
          state_changed_at: worker.state_changed_at,
        };
      });

      setWorkers(diagnostics);

      // Calculate stats - now based on safety state
      const total = diagnostics.length;
      const chronic = diagnostics.filter((w) => w.safety_state === "chronic").length;
      const highRisk = diagnostics.filter((w) => w.safety_state === "high_risk").length;
      const atRisk = diagnostics.filter((w) => w.safety_state === "at_risk").length;
      const safe = diagnostics.filter((w) => w.safety_state === "safe").length;
      const avgScore = total > 0 ? diagnostics.reduce((sum, w) => sum + w.current_risk_score, 0) / total : 0;
      const withProfile = diagnostics.filter(
        (w) => w.psychometric_profile && Object.keys(w.psychometric_profile).length > 0,
      ).length;

      setStats({
        totalWorkers: total,
        chronicCount: chronic,
        highRiskCount: highRisk,
        atRiskCount: atRisk,
        safeCount: safe,
        avgRiskScore: Math.round(avgScore),
        psychometricCoverage: total > 0 ? Math.round((withProfile / total) * 100) : 0,
      });
    } catch (error) {
      console.error("Error fetching worker diagnostics:", error);
      toast({ title: "Error", description: "Failed to load diagnostics", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const classifyRiskProfile = (
    psychProfile: Record<string, any>,
    riskScore: number,
    violationCount: number,
  ): string => {
    const upps = psychProfile?.upps_p || {};
    const dospert = psychProfile?.dospert_health_safety || 0;
    const delayDiscounting = psychProfile?.delay_discounting || 0;

    if (violationCount >= 3) {
      return "Repeat Offender";
    }
    if ((upps.lack_of_perseverance || 0) > 0.6 || (upps.negative_urgency || 0) > 0.6) {
      return "Impulsive";
    }
    if (dospert > 0.6 || (upps.sensation_seeking || 0) > 0.6) {
      return "Risk Taker";
    }
    if (delayDiscounting > 0.6) {
      return "Disengaged";
    }
    if (riskScore >= 60) {
      return "High Risk";
    }
    return "Standard";
  };

  const getKeyRiskFactors = (
    psychProfile: Record<string, any>,
    riskScore: number,
    networkRole: string | null,
    violationCount: number,
    isWideBridge: boolean,
  ): string[] => {
    const factors: string[] = [];
    const upps = psychProfile?.upps_p || {};

    if (violationCount >= 3) factors.push(`${violationCount} verified violations`);
    if (isWideBridge) factors.push("Wide bridge (spreads behavior)");
    if ((upps.negative_urgency || 0) > 0.5) factors.push("High negative urgency");
    if ((upps.lack_of_premeditation || 0) > 0.5) factors.push("Low premeditation");
    if ((upps.lack_of_perseverance || 0) > 0.5) factors.push("Low perseverance");
    if ((upps.sensation_seeking || 0) > 0.5) factors.push("High sensation seeking");
    if ((psychProfile?.dospert_health_safety || 0) > 0.5) factors.push("High risk tolerance");
    if ((psychProfile?.delay_discounting || 0) > 0.5) factors.push("Short-term focused");
    if (networkRole === "isolate") factors.push("Socially isolated");
    if (riskScore >= 60 && factors.length === 0) factors.push("Elevated behavioral risk");

    return factors.slice(0, 4);
  };

  const getRecommendedIntervention = (
    classification: string,
    riskScore: number,
    networkRole: string | null,
    isWideBridge: boolean,
  ): string | null => {
    if (riskScore < 30) return null;

    if (isWideBridge) return "Priority: Wide Bridge Intervention";

    switch (classification) {
      case "Repeat Offender":
        return "Supervisor meeting + commitment device";
      case "Impulsive":
        return "Micro-commitment popup";
      case "Risk Taker":
        return "Consequence learning quest";
      case "Disengaged":
        return "Gamified engagement nudge";
      case "High Risk":
        return "Comprehensive intervention";
      default:
        if (networkRole === "isolate") return "Peer connection nudge";
        if (networkRole === "champion") return "Champion deployment quest";
        return "Standard safety reminder";
    }
  };

  const recalculateRisk = async (workerId: string) => {
    setRecalculatingRisk(workerId);
    try {
      const { data, error } = await supabase.functions.invoke("predict-risk-score", {
        body: { workerId },
      });

      if (error) throw error;

      toast({
        title: "Risk Recalculated",
        description: `New score: ${data?.predictedRiskScore || "N/A"}% (${data?.riskLevel || "Unknown"})`,
      });

      // Refresh data
      await fetchWorkerDiagnostics();
    } catch (error) {
      console.error("Error recalculating risk:", error);
      toast({ title: "Error", description: "Failed to recalculate risk", variant: "destructive" });
    } finally {
      setRecalculatingRisk(null);
    }
  };

  const recalculateAllRisks = async () => {
    setRecalculatingAll(true);
    try {
      const highRiskWorkers = workers.filter((w) => w.current_risk_score >= 40).slice(0, 20);

      for (const worker of highRiskWorkers) {
        await supabase.functions.invoke("predict-risk-score", {
          body: { workerId: worker.id },
        });
      }

      toast({
        title: "Risk Scores Updated",
        description: `Recalculated ${highRiskWorkers.length} workers`,
      });

      await fetchWorkerDiagnostics();
    } catch (error) {
      console.error("Error recalculating risks:", error);
      toast({ title: "Error", description: "Failed to recalculate risks", variant: "destructive" });
    } finally {
      setRecalculatingAll(false);
    }
  };

  const getRiskColor = (score: number) => {
    if (score >= 60) return "text-destructive";
    if (score >= 30) return "text-warning";
    return "text-success";
  };

  const getRiskBadge = (score: number) => {
    if (score >= 60) return <Badge variant="destructive">High Risk</Badge>;
    if (score >= 30) return <Badge className="bg-warning text-warning-foreground">Medium</Badge>;
    return <Badge className="bg-success text-success-foreground">Low</Badge>;
  };

  const getClassificationBadge = (classification: string) => {
    switch (classification) {
      case "Repeat Offender":
        return <Badge variant="destructive">Repeat Offender</Badge>;
      case "Impulsive":
        return (
          <Badge variant="outline" className="border-orange-500 text-orange-500">
            Impulsive
          </Badge>
        );
      case "Risk Taker":
        return (
          <Badge variant="outline" className="border-red-500 text-red-500">
            Risk Taker
          </Badge>
        );
      case "Disengaged":
        return (
          <Badge variant="outline" className="border-blue-500 text-blue-500">
            Disengaged
          </Badge>
        );
      case "High Risk":
        return <Badge variant="destructive">High Risk</Badge>;
      default:
        return <Badge variant="secondary">Standard</Badge>;
    }
  };

  const getNetworkRoleBadge = (role: string | null, isWideBridge: boolean) => {
    if (isWideBridge) {
      return <Badge className="bg-destructive/20 text-destructive border-destructive/30">Wide Bridge</Badge>;
    }
    if (!role) return null;
    switch (role) {
      case "champion":
        return <Badge className="bg-success/20 text-success border-success/30">Champion</Badge>;
      case "bridge":
        return <Badge className="bg-primary/20 text-primary border-primary/30">Bridge</Badge>;
      case "isolate":
        return <Badge className="bg-warning/20 text-warning border-warning/30">Isolated</Badge>;
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  const getSafetyStateBadge = (state: string, large = false) => {
    const baseClasses = large ? "px-3 py-1.5 text-sm font-semibold" : "";
    switch (state) {
      case "chronic":
        return <Badge className={cn("bg-red-600 text-white hover:bg-red-700", baseClasses)}>Chronic</Badge>;
      case "high_risk":
        return <Badge className={cn("bg-orange-500 text-white hover:bg-orange-600", baseClasses)}>High Risk</Badge>;
      case "at_risk":
        return (
          <Badge className={cn("bg-warning text-warning-foreground hover:bg-warning/90", baseClasses)}>At Risk</Badge>
        );
      default:
        return (
          <Badge className={cn("bg-success text-success-foreground hover:bg-success/90", baseClasses)}>Safe</Badge>
        );
    }
  };

  const handleGenerateRecommendations = async () => {
    setGeneratingRecommendations(true);
    try {
      // Use new MDP-based edge function
      const { data, error } = await supabase.functions.invoke("generate-intervention-recommendations", {
        body: {
          companyId: workers[0]?.site_id ? undefined : undefined,
          generateForAll: true,
        },
      });

      if (error) throw error;

      toast({
        title: "MDP Recommendations Generated",
        description: `Created ${data?.recommendationsCreated || 0} AI-powered intervention suggestions`,
      });

      // Stay inside the standard Admin dashboard (no super-admin access required)
      navigate("/admin?tab=recommendations");
    } catch (error) {
      console.error("Error generating recommendations:", error);
      toast({ title: "Error", description: "Failed to generate recommendations", variant: "destructive" });
    } finally {
      setGeneratingRecommendations(false);
    }
  };

  const handleGenerateSingleRecommendation = async (workerId: string, workerName: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("generate-intervention-recommendations", {
        body: { workerId },
      });

      if (error) throw error;

      if (data?.recommendationsCreated > 0) {
        toast({
          title: "Recommendation Created",
          description: `MDP selected: ${data.recommendations[0]?.selectedAction || "intervention"} for ${workerName}`,
        });
        // Stay inside the standard Admin dashboard (no super-admin access required)
        navigate("/admin?tab=recommendations");
      } else {
        toast({
          title: "No Recommendation Needed",
          description: `${workerName} doesn't need intervention at this time`,
        });
      }
    } catch (error) {
      console.error("Error generating recommendation:", error);
      toast({ title: "Error", description: "Failed to generate recommendation", variant: "destructive" });
    }
  };

  const filteredWorkers = workers.filter((w) => {
    const matchesSearch =
      w.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      w.team.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesState = stateFilter === "all" || w.safety_state === stateFilter;

    return matchesSearch && matchesState;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-6 w-6 text-primary" />
            Worker Diagnostics
          </h2>
          <p className="text-muted-foreground">Identify high-risk workers and understand why they need intervention</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {!propSiteId && <SiteFilter value={selectedSite} onChange={setSelectedSite} />}
          <Button onClick={recalculateAllRisks} disabled={recalculatingAll} variant="outline" className="gap-2">
            <RefreshCw className={cn("h-4 w-4", recalculatingAll && "animate-spin")} />
            Recalculate All
          </Button>
          <Button onClick={handleGenerateRecommendations} disabled={generatingRecommendations} className="gap-2">
            <Sparkles className={cn("h-4 w-4", generatingRecommendations && "animate-spin")} />
            Generate Recommendations
          </Button>
        </div>
      </div>

      {/* Safety State Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-red-900/20 to-red-800/10 border-red-700/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Chronic</p>
                <p className="text-3xl font-bold text-red-400">{stats?.chronicCount}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-400/50" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Persistent unsafe behavior</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-600/20 to-orange-500/10 border-orange-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">High Risk</p>
                <p className="text-3xl font-bold text-orange-400">{stats?.highRiskCount}</p>
              </div>
              <Activity className="h-8 w-8 text-orange-400/50" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Needs intervention now</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-warning/10 to-warning/5 border-warning/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">At Risk</p>
                <p className="text-3xl font-bold text-warning">{stats?.atRiskCount}</p>
              </div>
              <Target className="h-8 w-8 text-warning/50" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Monitor closely</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-success/10 to-success/5 border-success/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Safe</p>
                <p className="text-3xl font-bold text-success">{stats?.safeCount}</p>
              </div>
              <Shield className="h-8 w-8 text-success/50" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">No action needed</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Psych Coverage</p>
                <p className="text-3xl font-bold">{stats?.psychometricCoverage}%</p>
              </div>
              <Brain className="h-8 w-8 text-muted-foreground/50" />
            </div>
            <Progress value={stats?.psychometricCoverage || 0} className="h-1.5 mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or team..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              {(
                [
                  { key: "all", label: "All" },
                  { key: "chronic", label: "Chronic" },
                  { key: "high_risk", label: "High Risk" },
                  { key: "at_risk", label: "At Risk" },
                  { key: "safe", label: "Safe" },
                ] as const
              ).map((filter) => (
                <Button
                  key={filter.key}
                  variant={stateFilter === filter.key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStateFilter(filter.key)}
                  className={cn(
                    filter.key === "chronic" && stateFilter === filter.key && "bg-red-600 hover:bg-red-700",
                    filter.key === "high_risk" && stateFilter === filter.key && "bg-orange-500 hover:bg-orange-600",
                    filter.key === "at_risk" && stateFilter === filter.key && "bg-warning hover:bg-warning/90",
                    filter.key === "safe" && stateFilter === filter.key && "bg-success hover:bg-success/90",
                  )}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Worker List */}
      <div className="space-y-3">
        {filteredWorkers.map((worker) => (
          <Card
            key={worker.id}
            className={cn(
              "transition-all hover:shadow-md cursor-pointer",
              worker.safety_state === "chronic" && "border-l-4 border-l-red-600",
              worker.safety_state === "high_risk" && "border-l-4 border-l-orange-500",
              worker.safety_state === "at_risk" && "border-l-4 border-l-warning",
              worker.safety_state === "safe" && "border-l-4 border-l-success",
              worker.is_wide_bridge && "ring-2 ring-destructive/50",
            )}
            onClick={() => setExpandedWorker(expandedWorker === worker.id ? null : worker.id)}
          >
            <CardContent className="p-4">
              {/* Main Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Safety State Badge - Now the primary highlight */}
                  <div className="flex flex-col items-center gap-1">
                    {getSafetyStateBadge(worker.safety_state, true)}
                    <span className="text-xs text-muted-foreground">{worker.current_risk_score}% score</span>
                  </div>

                  {/* Worker Info */}
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      {worker.display_name}
                      {worker.current_risk_score !== worker.baseline_risk_score &&
                        (worker.current_risk_score > worker.baseline_risk_score ? (
                          <TrendingUp className="h-3 w-3 text-destructive" />
                        ) : (
                          <TrendingDown className="h-3 w-3 text-success" />
                        ))}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {worker.team} • {worker.position || "Worker"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {getClassificationBadge(worker.risk_classification)}
                  {getNetworkRoleBadge(worker.network_role, worker.is_wide_bridge)}
                  {worker.violation_count > 0 && (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {worker.violation_count}
                    </Badge>
                  )}
                  {expandedWorker === worker.id ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedWorker === worker.id && (
                <div className="mt-4 pt-4 border-t space-y-4">
                  {/* PLAIN LANGUAGE SUMMARY - Always visible */}
                  <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-4 space-y-3">
                    <h4 className="font-semibold flex items-center gap-2 text-primary">
                      <Lightbulb className="h-4 w-4" />
                      Mengapa {worker.display_name} Perlu Perhatian?
                    </h4>
                    
                    {/* Detailed plain language explanation */}
                    <div className="space-y-3">
                      {/* Violations - be specific about what they did */}
                      {worker.violations.length > 0 && (
                        <div className="bg-destructive/5 rounded-lg p-3 border border-destructive/20">
                          <p className="text-sm font-medium text-destructive mb-2">
                            📋 Dilaporkan {worker.violation_count} kali dalam 30 hari terakhir:
                          </p>
                          <ul className="space-y-1.5">
                            {worker.violations.slice(0, 3).map((v, i) => {
                              const category = hazardCategoryTranslations[v.hazard_category];
                              return (
                                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                  <span className="text-destructive">•</span>
                                  <span>
                                    <strong>{category?.name || v.hazard_category.replace(/_/g, ' ')}</strong>
                                    {v.description && ` - "${v.description.slice(0, 60)}${v.description.length > 60 ? '...' : ''}"`}
                                    <span className="text-xs ml-1">({format(new Date(v.created_at), "d MMM")})</span>
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                      
                      {/* Network influence - explain why it matters */}
                      {(worker.is_wide_bridge || worker.network_role === 'champion' || worker.risky_neighbor_count > 0) && (
                        <div className="bg-purple-500/5 rounded-lg p-3 border border-purple-500/20">
                          <p className="text-sm font-medium text-purple-600 dark:text-purple-400 mb-2">
                            👥 Pengaruh di Tempat Kerja:
                          </p>
                          <ul className="space-y-1.5 text-sm text-muted-foreground">
                            {worker.is_wide_bridge && (
                              <li className="flex items-start gap-2">
                                <span className="text-purple-500">•</span>
                                <span>
                                  <strong>Punya banyak teman di berbagai tim</strong> - jika kebiasaan buruknya tidak diperbaiki, 
                                  bisa cepat menular ke orang lain di tim lain
                                </span>
                              </li>
                            )}
                            {worker.network_role === 'champion' && !worker.is_wide_bridge && (
                              <li className="flex items-start gap-2">
                                <span className="text-purple-500">•</span>
                                <span>
                                  <strong>Punya pengaruh besar</strong> - orang-orang sering meniru apa yang dia lakukan
                                </span>
                              </li>
                            )}
                            {worker.risky_neighbor_count > 0 && (
                              <li className="flex items-start gap-2">
                                <span className="text-purple-500">•</span>
                                <span>
                                  Sering bekerja bersama <strong>{worker.risky_neighbor_count} rekan yang juga bermasalah</strong> - 
                                  bisa saling mempengaruhi ke arah negatif
                                </span>
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                      
                      {/* Personality traits - translate to everyday behavior */}
                      {worker.psychometric_profile && (
                        (() => {
                          const upps = worker.psychometric_profile.upps_p || {};
                          const traits: { key: string; value: number; translation: typeof psychometricTranslations[string] }[] = [];
                          
                          if ((upps.sensation_seeking || 0) > 0.5) {
                            traits.push({ key: 'sensation_seeking', value: upps.sensation_seeking, translation: psychometricTranslations.sensation_seeking });
                          }
                          if ((upps.negative_urgency || 0) > 0.5) {
                            traits.push({ key: 'negative_urgency', value: upps.negative_urgency, translation: psychometricTranslations.negative_urgency });
                          }
                          if ((upps.lack_of_perseverance || 0) > 0.5) {
                            traits.push({ key: 'lack_of_perseverance', value: upps.lack_of_perseverance, translation: psychometricTranslations.lack_of_perseverance });
                          }
                          if ((upps.lack_of_premeditation || 0) > 0.5) {
                            traits.push({ key: 'lack_of_premeditation', value: upps.lack_of_premeditation, translation: psychometricTranslations.lack_of_premeditation });
                          }
                          if ((worker.psychometric_profile.dospert_health_safety || 0) > 0.5) {
                            traits.push({ key: 'dospert', value: worker.psychometric_profile.dospert_health_safety, translation: psychometricTranslations.dospert_health_safety });
                          }
                          
                          if (traits.length === 0) return null;
                          
                          return (
                            <div className="bg-warning/5 rounded-lg p-3 border border-warning/20">
                              <p className="text-sm font-medium text-warning mb-2">
                                🧠 Dari Kepribadian:
                              </p>
                              <ul className="space-y-1.5 text-sm text-muted-foreground">
                                {traits.slice(0, 2).map((trait, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-warning">•</span>
                                    <span>
                                      <strong>{trait.translation.title}</strong> - {trait.translation.workRisk.toLowerCase()}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })()
                      )}
                      
                      {/* Status explanation */}
                      <div className="bg-muted/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          {getSafetyStateBadge(worker.safety_state, false)}
                          <span className="text-sm font-medium">
                            {safetyStateTranslations[worker.safety_state]?.title || worker.safety_state}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {safetyStateTranslations[worker.safety_state]?.action || 'Lanjutkan monitoring'}
                        </p>
                      </div>
                      
                      {/* No risk factors */}
                      {worker.violation_count === 0 && !worker.is_wide_bridge && worker.risky_neighbor_count === 0 && (
                        <p className="text-sm text-success flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          Tidak ada faktor risiko signifikan yang terdeteksi saat ini
                        </p>
                      )}
                    </div>
                    
                    {/* Recommended Action */}
                    {worker.recommended_intervention && (
                      <div className="pt-3 border-t border-primary/20">
                        <h5 className="font-medium text-sm mb-1 flex items-center gap-2">
                          <Zap className="h-4 w-4 text-primary" />
                          Rekomendasi:
                        </h5>
                        <p className="text-sm text-primary">{worker.recommended_intervention}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* TECHNICAL DETAILS - Hidden by default */}
                  <Collapsible 
                    open={showTechnicalDetails === worker.id}
                    onOpenChange={(open) => setShowTechnicalDetails(open ? worker.id : null)}
                  >
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground">
                        <span className="flex items-center gap-2">
                          <Eye className="h-4 w-4" />
                          Lihat Detail Teknis
                        </span>
                        <ChevronDown className={cn("h-4 w-4 transition-transform", showTechnicalDetails === worker.id && "rotate-180")} />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-4">
                      <p className="text-xs text-muted-foreground italic">
                        Detail teknis untuk analisis mendalam oleh tim behavioral science
                      </p>
                      
                      {/* Network Analysis */}
                      <div>
                        <p className="text-sm font-medium mb-2 flex items-center gap-2">
                          <Network className="h-4 w-4 text-primary" />
                          Network Analysis
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="bg-muted/30 rounded-lg p-2">
                            <p className="text-xs text-muted-foreground">Role</p>
                            <p className="font-medium capitalize">{worker.network_role || "Not assigned"}</p>
                          </div>
                          <div className="bg-muted/30 rounded-lg p-2">
                            <p className="text-xs text-muted-foreground">Centrality</p>
                            <p className="font-medium">{worker.network_centrality?.toFixed(2) || "-"}</p>
                          </div>
                          <div className="bg-muted/30 rounded-lg p-2">
                            <p className="text-xs text-muted-foreground">Risky Neighbors</p>
                            <p className={cn("font-medium", worker.risky_neighbor_count > 0 && "text-warning")}>
                              {worker.risky_neighbor_count}
                            </p>
                          </div>
                          <div className="bg-muted/30 rounded-lg p-2">
                            <p className="text-xs text-muted-foreground">Cluster Risk</p>
                            <p
                              className={cn(
                                "font-medium",
                                worker.cluster_risk >= 60
                                  ? "text-destructive"
                                  : worker.cluster_risk >= 30
                                    ? "text-warning"
                                    : "text-success",
                              )}
                            >
                              {worker.cluster_risk > 0 ? `${Math.round(worker.cluster_risk)}%` : "-"}
                            </p>
                          </div>
                        </div>
                        {worker.is_wide_bridge && (
                          <div className="mt-2 p-2 rounded-lg bg-destructive/10 border border-destructive/20">
                            <p className="text-xs text-destructive flex items-center gap-1">
                              <Link2 className="h-3 w-3" />
                              Wide Bridge: Connects multiple clusters, behavior can spread rapidly
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Psychometric Summary */}
                      {worker.psychometric_profile && Object.keys(worker.psychometric_profile).length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2 flex items-center gap-2">
                            <Brain className="h-4 w-4 text-primary" />
                            Psychometric Profile
                          </p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {worker.psychometric_profile.upps_p && (
                              <>
                                <div className="bg-muted/30 rounded-lg p-2">
                                  <p className="text-xs text-muted-foreground">Negative Urgency</p>
                                  <p
                                    className={cn(
                                      "font-semibold",
                                      (worker.psychometric_profile.upps_p.negative_urgency || 0) > 0.6 &&
                                        "text-destructive",
                                    )}
                                  >
                                    {Math.round((worker.psychometric_profile.upps_p.negative_urgency || 0) * 100)}%
                                  </p>
                                </div>
                                <div className="bg-muted/30 rounded-lg p-2">
                                  <p className="text-xs text-muted-foreground">Lack of Premeditation</p>
                                  <p
                                    className={cn(
                                      "font-semibold",
                                      (worker.psychometric_profile.upps_p.lack_of_premeditation || 0) > 0.6 &&
                                        "text-destructive",
                                    )}
                                  >
                                    {Math.round((worker.psychometric_profile.upps_p.lack_of_premeditation || 0) * 100)}%
                                  </p>
                                </div>
                                <div className="bg-muted/30 rounded-lg p-2">
                                  <p className="text-xs text-muted-foreground">Sensation Seeking</p>
                                  <p
                                    className={cn(
                                      "font-semibold",
                                      (worker.psychometric_profile.upps_p.sensation_seeking || 0) > 0.6 && "text-warning",
                                    )}
                                  >
                                    {Math.round((worker.psychometric_profile.upps_p.sensation_seeking || 0) * 100)}%
                                  </p>
                                </div>
                              </>
                            )}
                            {worker.psychometric_profile.dospert_health_safety !== undefined && (
                              <div className="bg-muted/30 rounded-lg p-2">
                                <p className="text-xs text-muted-foreground">Risk Tolerance</p>
                                <p
                                  className={cn(
                                    "font-semibold",
                                    (worker.psychometric_profile.dospert_health_safety || 0) > 0.6 && "text-destructive",
                                  )}
                                >
                                  {Math.round((worker.psychometric_profile.dospert_health_safety || 0) * 100)}%
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Violation Details */}
                      {worker.violations.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2 flex items-center gap-2">
                            <FileWarning className="h-4 w-4 text-destructive" />
                            Recent Verified Violations ({worker.violation_count} total)
                          </p>
                          <div className="space-y-2">
                            {worker.violations.map((v) => (
                              <div key={v.id} className="bg-destructive/5 border border-destructive/20 rounded-lg p-2">
                                <div className="flex items-center justify-between">
                                  <Badge variant="outline" className="text-xs">
                                    {v.hazard_category}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    {format(new Date(v.created_at), "MMM d, yyyy")}
                                  </span>
                                </div>
                                <p className="text-sm mt-1 line-clamp-1">{v.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Key Risk Factors */}
                      <div>
                        <p className="text-sm font-medium mb-2 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-warning" />
                          Key Risk Factors
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {worker.key_risk_factors.length > 0 ? (
                            worker.key_risk_factors.map((factor, i) => (
                              <Badge key={i} variant="outline" className="bg-muted/50">
                                {factor}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">No significant risk factors</span>
                          )}
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Actions Row */}
                  <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      {worker.recommended_intervention && (
                        <>
                          <Zap className="h-4 w-4 text-primary" />
                          <span className="text-sm font-medium">{worker.recommended_intervention}</span>
                        </>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          recalculateRisk(worker.id);
                        }}
                        disabled={recalculatingRisk === worker.id}
                      >
                        {recalculatingRisk === worker.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        <span className="ml-1">Recalculate</span>
                      </Button>
                      {worker.recommended_intervention && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateSingleRecommendation(worker.id, worker.display_name);
                          }}
                        >
                          <Sparkles className="h-4 w-4 mr-1" />
                          AI Recommendation
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {filteredWorkers.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">No workers match your filters</CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default WorkerDiagnostics;
